import { Fragment } from 'react';
import { Link } from 'react-router-dom';

import { isolatePaths } from '@/lib/bidi/isolate-paths';

import { resolveDocLink } from './doc-links';

import type { DocInlineLinks } from './content/types';
import type { Translator } from '@/i18n/translator';
import type { Locale } from '@syncanix/shared';
import type { ReactNode } from 'react';

/**
 * INLINE-level rendering for the documentation block model, hoisted out of
 * `DocBlocks.tsx` (which was over the ~300-line ceiling in
 * `.claude/rules/architecture.md`). The cut is the file's own seam: `DocBlocks`
 * renders BLOCKS, this renders the runs of text inside them, and it is a leaf —
 * it imports nothing back from its parent.
 */

/**
 * Render a translated string, substituting `{token}` placeholders with their
 * configured links. Unmatched tokens are left verbatim (the translator already
 * passes unknown `{tokens}` through), so missing-link bugs are visible, not silent.
 */
export function renderInline(
  text: string,
  links: DocInlineLinks | undefined,
  t: Translator,
  locale: Locale,
): ReactNode {
  // `isolatePaths` on BOTH exits, not just the linked one: most docs prose
  // carries no `{token}` at all, and that is where 13 of the 15 measured
  // wrong-side slashes were.
  if (links === undefined) return isolatePaths(text);
  const parts = text.split(/(\{\w+\})/g);
  return parts.map((part, i) => {
    const match = /^\{(\w+)\}$/.exec(part);
    const link = match ? links[match[1] ?? ''] : undefined;
    if (match && link !== undefined) {
      const { href, external } = resolveDocLink(link.target, locale);
      const label = t(link.labelKey);
      // `--bd-link` (#2348db, 6.44:1 on white), NOT `--bd-blue`: the brand blue
      // is a FILL at 3.1:1, a WCAG AA contrast failure for body-size links
      // (`ui-design.md` § "`primary` is a FILL, `link` is TEXT").
      const className =
        'font-medium text-[color:var(--bd-link)] underline decoration-[color:color-mix(in_srgb,var(--bd-link)_30%,transparent)] underline-offset-[3px] transition-colors hover:decoration-[color:var(--bd-link)]';
      return external ? (
        <a key={i} href={href} target="_blank" rel="noopener noreferrer" className={className}>
          {label}
        </a>
      ) : (
        <Link key={i} to={href} className={className}>
          {label}
        </Link>
      );
    }
    return <Fragment key={i}>{isolatePaths(part)}</Fragment>;
  });
}
